import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { WS_CLOSE_NORMAL } from 'common/constants';
import { getBasePath } from 'utils/getBasePath';
import { registerNotificationsServiceWorker, showPersistentNotification } from 'utils/notifications.utils';

// How often the page records that it is still running
export const ACTIVITY_TICK_INTERVAL = 5000;

// A gap between ticks this large means the page was not executing - the machine slept or the
// tab was throttled. Background timers are throttled to ~1/min, so the threshold has to sit
// above that to avoid treating a merely hidden tab as an interruption on its own.
export const ACTIVITY_GAP_THRESHOLD = 90000;

// How long a reconnect is given to settle. The socket that died while the page was away only
// reports its close once the page runs again, which is after recovery has already started, so
// those closes must not be mistaken for a fresh failure.
export const RECOVERY_SETTLE_WINDOW = 10000;

const OFFLINE_NOTIFICATION_TITLE = "SLAI - You've gone inactive";
const OFFLINE_NOTIFICATION_BODY =
    "You won't receive new calls while this tab is in the background. Open the tab to go active again.";

type UseConnectionRecoveryParams = {
    /** Whether the agent was taking calls - nothing is recovered for an agent who went offline on purpose */
    agentAvailable: boolean;
    socketRef: MutableRefObject<WebSocket | null>;
    /** Set while the document is unloading, where a close is expected and must not be recovered */
    isUnloadingRef: MutableRefObject<boolean>;
    /** Puts the agent back online and reopens the socket */
    onReconnect: () => void;
};

type UseConnectionRecoveryResult = {
    /**
     * Call from the socket's `onClose`. Returns true when the disconnect happened while the
     * agent was away, meaning this hook owns the recovery and the caller should skip its own
     * disconnect handling.
     */
    handleSocketClose: () => boolean;
    /** Call from the socket's `onOpen` to drop a recovery that is no longer needed */
    clearPendingRecovery: () => void;
};

/**
 * Keeps an agent's session alive across interruptions they did not choose: Chrome freezing a
 * background tab, the laptop going to sleep, or the network dropping while the tab is hidden.
 *
 * All three end the same way - the backend stops seeing heartbeats, unregisters the client and
 * marks the agent offline - so the page notifies the agent and puts them back online once they
 * return.
 */
export const useConnectionRecovery = ({
    agentAvailable,
    socketRef,
    isUnloadingRef,
    onReconnect,
}: UseConnectionRecoveryParams): UseConnectionRecoveryResult => {
    const serviceWorkerRef = useRef<ServiceWorkerRegistration | null>(null);
    const lastActivityTickRef = useRef(Date.now());
    const isRecoveryPendingRef = useRef(false);
    const recoveryStartedAtRef = useRef(0);

    // Listeners are registered once, so the current values have to be readable from refs
    const agentAvailableRef = useRef(agentAvailable);
    const onReconnectRef = useRef(onReconnect);

    useEffect(() => {
        agentAvailableRef.current = agentAvailable;
        onReconnectRef.current = onReconnect;
    });

    // The worker has to be active before the interruption, so register it on mount
    useEffect(() => {
        let isMounted = true;

        registerNotificationsServiceWorker().then((registration) => {
            if (isMounted) {
                serviceWorkerRef.current = registration;
            }
        });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const tickId = setInterval(() => {
            lastActivityTickRef.current = Date.now();
        }, ACTIVITY_TICK_INTERVAL);

        return () => clearInterval(tickId);
    }, []);

    const notifyDisconnected = useCallback(() => {
        showPersistentNotification(serviceWorkerRef.current, OFFLINE_NOTIFICATION_TITLE, {
            // A fixed tag silently replaces a banner that is still in Notification Center,
            // so every disconnect needs its own tag to surface
            tag: `agent-offline-${Date.now()}`,
            body: OFFLINE_NOTIFICATION_BODY,
            data: { url: getBasePath() || '/' },
        });
    }, []);

    const runRecovery = useCallback(() => {
        isRecoveryPendingRef.current = false;
        recoveryStartedAtRef.current = Date.now();
        lastActivityTickRef.current = Date.now();
        onReconnectRef.current();
    }, []);

    const handleSocketClose = useCallback(() => {
        if (!agentAvailableRef.current || isUnloadingRef.current) {
            return false;
        }

        // The dead socket from before the interruption reports its close after recovery is
        // already under way, so swallow it rather than treating it as a new disconnect
        if (recoveryStartedAtRef.current && Date.now() - recoveryStartedAtRef.current < RECOVERY_SETTLE_WINDOW) {
            return true;
        }

        // A close while the page is running and visible is a real connection problem the agent
        // can see and act on - leave it to the caller's existing handling
        const wasAway = document.hidden || Date.now() - lastActivityTickRef.current > ACTIVITY_GAP_THRESHOLD;
        if (!wasAway) {
            return false;
        }

        if (document.hidden) {
            notifyDisconnected();
            isRecoveryPendingRef.current = true;
            return true;
        }

        // Already back - a machine waking from sleep only reports the close once the page runs
        // again, by which point the return events have fired
        runRecovery();
        return true;
    }, [isUnloadingRef, notifyDisconnected, runRecovery]);

    const clearPendingRecovery = useCallback(() => {
        isRecoveryPendingRef.current = false;
        recoveryStartedAtRef.current = 0;
        lastActivityTickRef.current = Date.now();
    }, []);

    // `freeze` is the last stack a page gets before Chrome stops it: no timers, no heartbeat
    // and no close handling until it thaws. Fires on `document`, not `window`.
    useEffect(() => {
        const handleFreeze = () => {
            if (!agentAvailableRef.current || isUnloadingRef.current) {
                return;
            }

            isRecoveryPendingRef.current = true;
            notifyDisconnected();

            // A frozen page cannot answer the heartbeat, so close now instead of leaving the
            // connection half-open until the backend's 130s read deadline. Closing normally also
            // keeps the socket library from retrying, which it cannot do while frozen anyway.
            const socket = socketRef.current;
            if (socket?.readyState === WebSocket.OPEN) {
                socket.close(WS_CLOSE_NORMAL, 'freeze');
            }
        };

        document.addEventListener('freeze', handleFreeze);

        return () => document.removeEventListener('freeze', handleFreeze);
    }, [isUnloadingRef, notifyDisconnected, socketRef]);

    // Every way of coming back: thawing a frozen tab, waking the machine, refocusing the
    // window or regaining the network
    useEffect(() => {
        const handleReturn = () => {
            // `resume` can fire while the tab is still in the background, where reconnecting
            // would only get the agent dropped again
            if (!isRecoveryPendingRef.current || document.hidden) {
                return;
            }

            runRecovery();
        };

        // Restoring from the back-forward cache is its own case: the browser tears the socket
        // down on the way in ("Page entered Back-Forward Cache") without the page ever
        // unloading, so there is nothing pending to resume - the session just needs rebuilding
        const handlePageShow = (event: PageTransitionEvent) => {
            if (!event.persisted || !agentAvailableRef.current) {
                return;
            }

            // The document was cached rather than unloaded, so the flag the unload handler set
            // no longer reflects reality and would otherwise block every later recovery
            isUnloadingRef.current = false;

            runRecovery();
        };

        document.addEventListener('resume', handleReturn);
        document.addEventListener('visibilitychange', handleReturn);
        window.addEventListener('focus', handleReturn);
        window.addEventListener('online', handleReturn);
        window.addEventListener('pageshow', handlePageShow);

        return () => {
            document.removeEventListener('resume', handleReturn);
            document.removeEventListener('visibilitychange', handleReturn);
            window.removeEventListener('focus', handleReturn);
            window.removeEventListener('online', handleReturn);
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [isUnloadingRef, runRecovery]);

    return { handleSocketClose, clearPendingRecovery };
};
